/*
 * SoundWave.tsx
 * Animated sound wave visualization with vertical bars
 */
'use client';

import React, {
  useRef,
  useImperativeHandle,
  forwardRef,
  useState,
  useEffect,
  useCallback,
} from 'react';

import { gsap } from 'gsap';
import { useDebug } from '@/utils/useDebugLog';
import { useViewSize } from '@/providers/ViewSizeContext';
import { useClientMount } from '@/providers/ClientMountProvider';

// Config
import { DEBUG_LOGS } from '../../config/constants';

// Assets
import audioData from './data/audio-data.json';

// Styles
import styles from './SoundWave.module.scss';
import classNames from 'classnames/bind';
const cx = classNames.bind(styles);

export interface SoundWaveRef {
  start: () => void;
  stop: () => void;
}

interface SoundWaveProps {
  animationDuration?: number; // Duration in seconds
}

type BarConfig = {
  x: number;
  min: number;
  max: number;
};

const DRAMA = 4; // Amplifies the frequency data

// Controls how often audio frames update
// Ex: 1 = fastest, 3 = slower
const FRAME_SPEED = 8; // 8;

// Controls how fast bars animate
// Ex: 0.08 = fast, 2 = slow
const BAR_SPEED = 0.3; // 1;

// Helper: Get interpolated frequency for a given bar index
function getInterpolatedFrequency(
  frequencies: number[],
  barIndex: number,
  totalBars: number
) {

  if (!frequencies.length || totalBars <= 1) return 0;

  const lastBarIndex = totalBars - 1;
  const u = lastBarIndex > 0 ? barIndex / lastBarIndex : 0; // 0..1 across bars

  let mappedU: number;
  if (u <= 0.5) {
    // Left side: 0 -> 0.5, center -> 0
    mappedU = 0.5 - u;
  } else {
    // Right side: center -> 1, 1 -> 0.5
    mappedU = 1.5 - u;
  }

  const maxFreqIndex = frequencies.length - 1;
  const sourceIndex = mappedU * maxFreqIndex;

  const lowerIndex = Math.floor(sourceIndex);
  const upperIndex = Math.min(maxFreqIndex, Math.ceil(sourceIndex));
  const fraction = sourceIndex - lowerIndex;

  const lower = frequencies[lowerIndex] || 0;
  const upper = frequencies[upperIndex] || lower;

  return lower + (upper - lower) * fraction;
}

const SoundWave = forwardRef<SoundWaveRef, SoundWaveProps>(({ animationDuration = 1.5 }, ref) => {

  const { log } = useDebug({ name: 'SoundWave', bgColor: 'cyan', enabled: false/* DEBUG_LOGS */ });

  // SSR guards
  const { isReady: isViewSizeReady } = useViewSize();
  const { mounted } = useClientMount();

  // Refs and state
  const rootRef = useRef<HTMLDivElement>(null);
  const barRefs = useRef<(SVGRectElement | null)[]>([]);
  const [barConfig, setBarConfig] = useState<BarConfig[]>([]);
  const frameIndex = useRef(0);
  const raf = useRef<number | null>(null);
  const stopTimer = useRef<NodeJS.Timeout | null>(null);

  // SVG size (viewBox dimensions)
  const svgSize = {
    width: 128 * 8,
    height: 24 * 8
  };

  // Bar dimensions in SVG units (proportional to viewBox)
  const svgBarWidth = 6; // Width of each bar in SVG units
  const svgGap = 4; // Gap between bars in SVG units

  // ---------- Build bars based on SVG viewBox

  useEffect(() => {
    // Calculate number of bars that fit in the SVG viewBox
    const count = Math.floor((svgSize.width + svgGap) / (svgBarWidth + svgGap));

    const minH = svgSize.height * 0.1;
    const maxH = svgSize.height * 0.9;

    const arr = Array.from({ length: count }, (_, i) => {
      const x = i * (svgBarWidth + svgGap);
      return { x, min: minH, max: maxH };
    });

    setBarConfig(arr);
    log('Generated bars', { count, svgWidth: svgSize.width });
  }, []);

  // ---------- Set initial bar heights to 0

  useEffect(() => {
    if (barConfig.length === 0 || barRefs.current.length < barConfig.length) return;

    // Set all bars to height 0 (centered)
    barRefs.current.forEach((bar, i) => {
      if (!bar) return;

      gsap.set(bar, {
        attr: {
          height: 0,
          y: svgSize.height / 2
        }
      });
    });

    log('Set initial bar heights to 0');
  }, [barConfig]);

  // ---------- Animation Controls

  // Loop animation: Stop
  const stop = useCallback(() => {
    log('stop');

    if (raf.current) cancelAnimationFrame(raf.current);
    raf.current = null;

    if (stopTimer.current) clearTimeout(stopTimer.current);
    stopTimer.current = null;

    // Important: kill tweens to prevent leftover animation
    barRefs.current.forEach(bar => bar && gsap.killTweensOf(bar));
  }, []);

  // Loop animation: Frame
  const animateFrame = useCallback(() => {
    const frames = audioData?.frames;
    if (!frames || frames.length === 0) return;

    // Slow the animation by skipping frames
    if (frameIndex.current % FRAME_SPEED === 0) {
      const frame = frames[Math.floor(frameIndex.current / FRAME_SPEED) % frames.length];
      const freqs = frame.frequencies || [];

      const maxFreq = Math.max(...freqs.filter(f => f > 0), 1);

      // Apply heights to bars
      barRefs.current.forEach((bar, i) => {
        if (!bar || !barConfig[i]) return;

        const cfg = barConfig[i];
        const raw = getInterpolatedFrequency(freqs, i, barRefs.current.length);
        let n = raw / maxFreq;

        const centered = n - 0.5;
        n = Math.max(0, Math.min(1, centered * DRAMA + 0.5));

        const h = cfg.min + n * (cfg.max - cfg.min);
        const y = (svgSize.height - h) / 2;

        gsap.to(bar, {
          attr: { height: h, y },
          duration: BAR_SPEED, // 0.08,
          ease: 'sine.out',
        });
      });
    }

    frameIndex.current++;
    raf.current = requestAnimationFrame(animateFrame);
  }, [barConfig]);

  // Loop animation: Start
  const start = useCallback(() => {
    if (barConfig.length === 0) {
      log('start skipped no bars');
      return;
    }

    stop();
    frameIndex.current = 0;
    raf.current = requestAnimationFrame(animateFrame);

    // Stop animation after animationDuration seconds
    stopTimer.current = setTimeout(() => {
      log('Auto-stopping SoundWave after duration');
      stop();
    }, animationDuration * 1000);
  }, [barConfig, stop, animateFrame, animationDuration]);

  // ---------- Expose start/stop methods

  useImperativeHandle(ref, () => ({ start, stop }));

  // -------------------- Render

  if (!mounted || !isViewSizeReady) return null;

  return (
    <div ref={rootRef} className={cx('sound-wave')}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox={`0 0 ${svgSize.width} ${svgSize.height}`}
        width="100%"
        height="100%"
        style={{ display: 'block' }}
      >
        {barConfig.map((cfg, i) => (
          <rect
            key={i}
            ref={el => { barRefs.current[i] = el; }}
            x={cfg.x}
            y={svgSize.height / 2}
            width={svgBarWidth}
            height={2}
            fill="currentColor"
            rx={4}
            ry={4}
          />
        ))}
      </svg>
    </div>
  );

});

export default SoundWave;
